import { useEffect, useState } from 'react';
import { Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { Content, Header, InfoCard, Page, Progress, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

type Summary = {
  days: number;
  users: number;
  pages: { page: string; visits: string | number; users: string | number }[];
  daily: { day: string; users: string | number }[];
};

const columns: TableColumn<Summary['pages'][number]>[] = [
  { title: 'Página', field: 'page' },
  { title: 'Visitas', field: 'visits', type: 'numeric' },
  { title: 'Personas distintas', field: 'users', type: 'numeric' },
];

/** Qué partes de Backstage se usan y cuánta gente entra; solo cifras agregadas. */
export const UsagePage = () => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<Summary>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    setSummary(undefined);
    (async () => {
      const res = await fetch(`${await discovery.getBaseUrl('usage')}/summary?days=${days}`);
      if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
      setSummary(await res.json());
    })().catch(e => setError(String(e?.message ?? e)));
  }, [days, discovery, fetch]);

  const avg = summary?.daily.length
    ? Math.round(summary.daily.reduce((n, d) => n + Number(d.users), 0) / summary.daily.length)
    : 0;

  return (
    <Page themeId="tool">
      <Header title="Uso de Backstage" subtitle="Qué se usa y cuánta gente entra, para decidir qué mejorar" />
      <Content>
        <Select value={days} onChange={e => setDays(Number(e.target.value))} style={{ marginBottom: 16 }}>
          {[7, 30, 90].map(d => (
            <MenuItem key={d} value={d}>Últimos {d} días</MenuItem>
          ))}
        </Select>
        {error && <WarningPanel title="No se pudo cargar el uso" message={error} />}
        {!summary && !error && <Progress />}
        {summary && (
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <InfoCard title="Personas distintas">
                <Typography variant="h3">{summary.users}</Typography>
              </InfoCard>
            </Grid>
            <Grid item md={6} xs={12}>
              <InfoCard title="Promedio por día (con visitas)">
                <Typography variant="h3">{avg}</Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12}>
              <Table
                title="Páginas más usadas"
                options={{ paging: false, search: true, padding: 'dense' }}
                columns={columns}
                data={summary.pages}
                emptyContent={<div style={{ padding: 16 }}>Todavía no hay datos: se empiezan a contar desde este despliegue.</div>}
              />
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
